<template>
  <div data-test="investment-mix-stacked" class="relative h-44">
    <div
      v-if="series.length < 2"
      class="absolute inset-0 flex items-center justify-center text-sm"
      style="color: var(--fg-muted)"
      data-test="stacked-empty"
    >
      Need 2+ closed sprints with M4 ratio
    </div>
    <Line v-else :data="chartData" :options="chartOptions" />
  </div>
</template>

<script setup lang="ts">
import "@/lib/chartSetup";
import { computed } from "vue";
import { Line } from "vue-chartjs";
import type { ChartData, ChartOptions } from "chart.js";
import { cssVar } from "@/lib/chartTokens";
import type { MixPoint } from "@/lib/investmentMix";

const props = defineProps<{
  series: MixPoint[];
}>();

const labels = computed(() => props.series.map((p) => p.sprintName));

const chartData = computed<ChartData<"line", number[], string>>(() => {
  const accent = cssVar("--accent");
  const amber = cssVar("--rag-amber");
  const green = cssVar("--rag-green");
  return {
    labels: labels.value,
    datasets: [
      {
        label: "F7",
        data: props.series.map((p) => p.f7),
        borderColor: accent,
        backgroundColor: hexA(accent, 0.55),
        fill: "origin",
        pointRadius: 0,
        tension: 0.3,
      },
      {
        label: "BAU",
        data: props.series.map((p) => p.bau),
        borderColor: amber,
        backgroundColor: hexA(amber, 0.55),
        fill: "-1",
        pointRadius: 0,
        tension: 0.3,
      },
      {
        label: "Stability",
        data: props.series.map((p) => p.stability),
        borderColor: green,
        backgroundColor: hexA(green, 0.55),
        fill: "-1",
        pointRadius: 0,
        tension: 0.3,
      },
    ],
  };
});

const chartOptions = computed<ChartOptions<"line">>(() => {
  const grid = cssVar("--grid");
  const fgMuted = cssVar("--fg-muted");
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        display: true,
        position: "bottom",
        labels: { color: fgMuted, boxWidth: 14, font: { size: 11 } },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const v = ctx.parsed.y ?? 0;
            return `${ctx.dataset.label}: ${(v * 100).toFixed(1)}%`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: { color: fgMuted, font: { size: 10 } },
        grid: { color: grid },
      },
      y: {
        stacked: true,
        min: 0,
        max: 1,
        ticks: {
          color: fgMuted,
          font: { size: 10 },
          callback: (v) => `${Math.round((v as number) * 100)}%`,
        },
        grid: { color: grid },
      },
    },
  };
});

// Convert "#rrggbb" to "rgba(r,g,b,a)" so stacked-area fills can be translucent.
// Pass through anything that isn't a recognisable hex (rgba/hsl from CSS vars).
function hexA(color: string, alpha: number): string {
  const m = color.trim().match(/^#([0-9a-f]{6})$/i);
  if (!m) return color;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 0xff;
  const g = (n >> 8) & 0xff;
  const b = n & 0xff;
  return `rgba(${r},${g},${b},${alpha})`;
}

defineExpose({ chartData, chartOptions });
</script>
